import type { ReactElement } from 'react';
import type { SceneCoverage } from '@scoutaround/script-breakdown-shared';

import { SCENE_COVERAGE_LABEL, SCENE_COVERAGE_SENTENCE } from './labels.js';

/**
 * HOW MUCH OF THE SCENE THE AI READING EXAMINED — a separate fact from whether a person has
 * reviewed it, and drawn apart from the review dot so the two are never read as one.
 *
 * Each coverage has its own SHAPE (a full disc, a disc with a notch, a half, an empty ring, a ring
 * with a clock hand, a dashed ring, a dotted ring), so it never rests on colour alone, and its
 * sentence is the mark's accessible name and title.
 */
const SHAPES: Readonly<Record<SceneCoverage, (colour: string) => ReactElement>> = {
  FULL: (c) => <circle cx="8" cy="8" r="5.5" fill={c} />,
  SELECTIVE: (c) => (
    <>
      <circle cx="8" cy="8" r="5.5" fill="none" stroke={c} strokeWidth="1.5" />
      <path d="M8 2.5A5.5 5.5 0 0 1 13.5 8H8z M8 8v5.5A5.5 5.5 0 0 1 2.5 8z" fill={c} />
    </>
  ),
  PARTIAL: (c) => (
    <>
      <circle cx="8" cy="8" r="5.5" fill="none" stroke={c} strokeWidth="1.5" />
      <path d="M8 2.5a5.5 5.5 0 0 1 0 11z" fill={c} />
    </>
  ),
  NOT_READ: (c) => <circle cx="8" cy="8" r="5.25" fill="none" stroke={c} strokeWidth="1.5" />,
  OUT_OF_DATE: (c) => (
    <>
      <circle cx="8" cy="8" r="5.25" fill="none" stroke={c} strokeWidth="1.5" />
      <path d="M8 5v3.2l2 1.3" fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" />
    </>
  ),
  NOT_RECORDED: (c) => <circle cx="8" cy="8" r="5.25" fill="none" stroke={c} strokeWidth="1.5" strokeDasharray="2.2 2" />,
  NOT_ANALYSED: (c) => <circle cx="8" cy="8" r="5.25" fill="none" stroke={c} strokeWidth="1.5" strokeDasharray="0.1 2.6" strokeLinecap="round" />,
};

const TONE: Readonly<Record<SceneCoverage, string>> = {
  FULL: 'ok',
  SELECTIVE: 'ok',
  PARTIAL: 'warn',
  NOT_READ: 'muted',
  OUT_OF_DATE: 'outdated',
  NOT_RECORDED: 'muted',
  NOT_ANALYSED: 'muted',
};

export function CoverageMark({
  coverage,
  withLabel = false,
  testId,
}: {
  coverage: SceneCoverage;
  /** Print the word beside the shape (the reader); a dense row carries the shape alone. */
  withLabel?: boolean;
  testId?: string;
}) {
  const sentence = SCENE_COVERAGE_SENTENCE[coverage];

  return (
    <span
      className="el-coverage"
      data-coverage={coverage}
      data-tone={TONE[coverage]}
      role="img"
      aria-label={sentence}
      title={sentence}
      data-testid={testId}
    >
      <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
        {SHAPES[coverage]('currentColor')}
      </svg>
      {withLabel ? <span className="el-coverage-label">{`AI: ${SCENE_COVERAGE_LABEL[coverage]}`}</span> : null}
    </span>
  );
}
